/**
 * ffmpeg command synthesis. Takes a probe report + profile and returns a
 * copy-pasteable command line for running ffmpeg locally. Pure text — no
 * runtime dependency on ffmpeg itself.
 */

import type { TranscodeProfile, VideoProbeReport } from './types';

export interface SizeEstimate {
  bytes: number;
  confidence: 'high' | 'low';
  explanation: string;
}

function needsDownscale(report: VideoProbeReport, profile: TranscodeProfile): boolean {
  const v = report.video;
  if (!v) return false;
  return Math.max(v.width, v.height) > Math.max(profile.video.maxWidth, profile.video.maxHeight);
}

function scaleFilter(profile: TranscodeProfile): string {
  const { maxWidth, maxHeight } = profile.video;
  return `scale='min(${maxWidth},iw)':'min(${maxHeight},ih)':force_original_aspect_ratio=decrease:force_divisible_by=2`;
}

function h264ProfileFlag(profile: TranscodeProfile): string {
  return profile.video.profile.toLowerCase();
}

export function buildFfmpegArgs(report: VideoProbeReport, profile: TranscodeProfile, inputName = 'input.mp4', outputName = 'output.mp4'): string[] {
  const args: string[] = ['-y', '-i', inputName];

  const filters: string[] = [];
  if (report.video?.isAnamorphic) {
    // Bake display aspect into coded dimensions so output has SAR 1:1.
    // Must come before any scale filter so downscale targets work against display dims.
    filters.push('scale=iw*sar:ih,setsar=1:1');
  }
  if (needsDownscale(report, profile)) {
    filters.push(scaleFilter(profile));
  }
  const framerate = report.video?.framerate;
  if (framerate != null && framerate > profile.video.maxFramerate) {
    filters.push(`fps=${profile.video.maxFramerate}`);
  }
  if (filters.length) {
    args.push('-vf', filters.join(','));
  }

  args.push(
    '-c:v', 'libx264',
    '-profile:v', h264ProfileFlag(profile),
    '-level', profile.video.maxLevel,
    '-preset', 'medium',
    '-b:v', String(profile.video.targetBitrateBps),
    '-maxrate', String(Math.round(profile.video.targetBitrateBps * 1.25)),
    '-bufsize', String(profile.video.targetBitrateBps * 2),
    '-pix_fmt', 'yuv420p',
    '-color_range', 'tv',
  );

  if (profile.audio) {
    args.push(
      '-c:a', 'aac',
      '-b:a', String(profile.audio.maxBitrateBps),
      '-ac', String(profile.audio.maxChannels),
      '-ar', String(profile.audio.maxSampleRateHz),
    );
  } else {
    args.push('-an');
  }

  args.push('-movflags', '+faststart', outputName);
  return args;
}

export function buildFfmpegCommand(report: VideoProbeReport, profile: TranscodeProfile, inputName = 'input.mp4', outputName = 'output.mp4'): string {
  const args = buildFfmpegArgs(report, profile, inputName, outputName);
  return ['ffmpeg', ...args.map(quoteShellArg)].join(' ');
}

function quoteShellArg(arg: string): string {
  if (/^[A-Za-z0-9_./:=+-]+$/.test(arg)) return arg;
  return `'${arg.replace(/'/g, `'\\''`)}'`;
}

export function estimateOutputSize(report: VideoProbeReport, profile: TranscodeProfile): SizeEstimate {
  const duration = report.durationSec || report.video?.durationSec || 0;
  if (duration <= 0) {
    return {
      bytes: 0,
      confidence: 'low',
      explanation: 'Duration unknown — cannot estimate output size.',
    };
  }
  const videoBitrate = profile.video.targetBitrateBps;
  const audioBitrate = profile.audio?.maxBitrateBps ?? 0;
  const bytes = Math.round(((videoBitrate + audioBitrate) * duration) / 8);
  return {
    bytes,
    confidence: 'high',
    explanation: `${formatBitrate(videoBitrate + audioBitrate)} × ${duration.toFixed(1)}s`,
  };
}

function formatBitrate(bps: number): string {
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(2)} Mbps`;
  return `${Math.round(bps / 1000)} kbps`;
}

export function formatSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
